import React, { useEffect, useMemo, useState } from 'react';

import { ReptoolModal } from '../../utilities/ReptoolModal.tsx';
import { CollectionReptile, TreeIndividual } from '../types/CollectionTypes.ts';
import { Form, Tab, Tabs, Button } from 'react-bootstrap';
import { BasicFetch } from '../../../utils/BasicFetch.ts';
import { MorphGeneInputs } from '../../utilities/MorphGeneInputs.tsx';
import { MorphSelect } from '../../utilities/MorphSelect.tsx';
import { Morph } from '../../../types/Morph.ts';
import { Gene, blankGene } from '../../../types/Gene.ts';
import { SortAndUploadPhotos, setGridControls } from './PictureModal.tsx';
import { useRepToolContext } from '../../../RepToolContext.tsx';

interface FamilyIndividualEditModalProps {
  closeModal: () => void;
  reptile: CollectionReptile;
  setReptile: (reptile: CollectionReptile) => void;
  editingFamilyIndividual: TreeIndividual;
  morphs: Morph[];
  genes: Gene[];
}
type TabTypes = 'details' | 'photos';

export const FamilyIndividualEditModal: React.FC<FamilyIndividualEditModalProps> = ({ closeModal, reptile, setReptile, editingFamilyIndividual, morphs, genes }) => {
  const [actveTab, setActiveTab] = useState<TabTypes>('details');
  const [currentEditingFamilyIndividual, setCurrentEditingFamilyIndividual] = useState<TreeIndividual>(editingFamilyIndividual);
  const [unlistMorph, setUnlistMorph] = useState<Morph | null>(null);
  const [activeUnlistedTab, setActiveUnlistedTab] = useState<'morph' | 'gene'>(editingFamilyIndividual.genes?.length ? 'gene' : 'morph');
  const [unlistGenes, setUnlistGenes] = useState<Gene[]>(editingFamilyIndividual.genes?.length ? editingFamilyIndividual.genes : [blankGene]);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [rearrangeOrder, setRearrangeOrder] = React.useState(editingFamilyIndividual.pictures);
  const [rearrangeActive, setRearrangeActive] = React.useState(false);
  const { appendControls } = useRepToolContext();

  const reptileId = useMemo(() => reptile.id, [reptile]);
  const orderChanged = useMemo(() => editingFamilyIndividual.pictures !== rearrangeOrder, [rearrangeOrder, editingFamilyIndividual.pictures]);


  const uploadPhoto = (photo: Blob) => {
    if (!photo) return;
    let formData = new FormData();
    let filename = `${new Date().toISOString()}.jpg`
    formData.append("file", photo, filename);
    if (editingFamilyIndividual.indiv_id) {
      formData.append("return_collection_reptile_id", reptileId.toString())
    }
    const url = editingFamilyIndividual.indiv_id ? `${(window as any).serverLocation}/collection_reptile_photo/${editingFamilyIndividual.indiv_id}/create` : `${(window as any).serverLocation}/collection_reptile/tree/${reptileId}/family/${editingFamilyIndividual.id}/upload`
    setUploadingPicture(true);
    BasicFetch({
      url,
      data: formData,
      method: 'POST',
      contentType: 'multipart/form-data',
      onSuccess: (data) => {
        setReptile(data.reptile);
      },
      onFail: () => {
        window.alert("There was a problem uploading your photo. Please try again later.")
      },
      onFinish: () => {
        setUploadingPicture(false);
      }
    })
  }

  const removePicture = (id) => {
    setUploadingPicture(true);
    const url = editingFamilyIndividual.indiv_id ? `${(window as any).serverLocation}/collection_reptile_photo/${reptile.id}/${id}/delete`
      : `${(window as any).serverLocation}/collection_reptile/tree/${reptileId}/family/${editingFamilyIndividual.id}/${id}/delete`

    BasicFetch({
      url,
      data: {},
      extra: {
        new_serialize: 'true'
      },
      method: 'Delete',
      onSuccess: (data) => {
        setReptile(data.reptile);
      },
      onFail: () => {
        window.alert("There was a problem removing your photo. Please try again later.")
      },
      onFinish: () => {
        setUploadingPicture(false);
      }
    })
  }
  const saveChanges = () => {
    const url = `${(window as any).serverLocation}/collection_reptile/tree/${reptileId}/family/${editingFamilyIndividual.id}/update`
    const data = {
      name: currentEditingFamilyIndividual.name,
      breeder: currentEditingFamilyIndividual.breeder,
      morph_name_id: unlistMorph?.morph_name_id,
      genes: unlistGenes.map(gene => {
        return {
          id: gene.gene_id,
          expression: gene.expression,
        }
      }),
    }

    BasicFetch({
      url,
      data,
      method: 'POST',
      onSuccess: (data) => {
        setReptile(data.reptile);
        window.alert("Changes saved successfully.");
      },
      onFail: () => {
        window.alert("There was a problem saving your changes. Please try again later.")
      }
    })

  }

  const saveOrder = () => {
    const data = { new_order: rearrangeOrder.map((image) => image.id), new_serialize: 'true', return_collection_reptile_id: reptileId };
    const url = editingFamilyIndividual.indiv_id ?
      `${(window as any).serverLocation}/collection_reptile_photo/${editingFamilyIndividual.indiv_id}/update_priorities` :
      `${(window as any).serverLocation}/collection_reptile/tree/${editingFamilyIndividual.id}/update_priorities`

    BasicFetch({
      url,
      method: 'PATCH',
      data: data,
      contentType: 'application/json',
      onSuccess: (data: { reptile: CollectionReptile }) => {
        setReptile(data.reptile);
      },
      onFail: () => {
        window.alert("There was a problem saving your photo order. Please try again later.")
      }
    })
  }


  useEffect(() => {
    if (actveTab === 'details') {
      appendControls(<div className='d-flex w-100'>
        <Button variant="outline-light" className='mx-auto' onClick={saveChanges}>Save Changes</Button>
      </div>, 'Edit Family Individual Details');
    } else if (actveTab === 'photos') {
      setGridControls(rearrangeActive, setRearrangeActive, orderChanged, false)
    } else {
      appendControls(null, 'Edit Family Individual else');
    }
  }, [actveTab, unlistGenes, unlistMorph, currentEditingFamilyIndividual, rearrangeOrder, orderChanged, rearrangeActive]);

  return (
    <ReptoolModal
      id="family-individual-photos-modal"
      title="Family Individual Photos"
      body={
        <div>
          <Tabs
            id="family-individual-photos-tabs"
            activeKey={actveTab}
            onSelect={(k) => setActiveTab(k)}
          >
            <Tab eventKey='details' title="Details">
              <div>
                {/* editable form fields for name and breeder */}
                <div className='d-flex flex-column'>
                  <div className='mt-2'>
                    <label>Name</label>
                    <Form.Control
                      type='text'
                      value={currentEditingFamilyIndividual.name}
                      onChange={(e) => {
                        setCurrentEditingFamilyIndividual({
                          ...currentEditingFamilyIndividual,
                          name: e.target.value
                        })
                      }}
                    />
                  </div>
                  <div className='mt-2'>
                    <label>Breeder Name</label>
                    <Form.Control
                      type='text'
                      value={currentEditingFamilyIndividual.breeder}
                      onChange={(e) => {
                        setCurrentEditingFamilyIndividual({
                          ...currentEditingFamilyIndividual,
                          breeder: e.target.value
                        })
                      }}
                    />
                  </div>
                </div>
                <hr />
                <div>
                  <Tabs defaultActiveKey={activeUnlistedTab} id="parent-genetics" onSelect={(value) => value === "gene" || value === "morph" ? setActiveUnlistedTab(value) : false} className="mx-auto">
                    <Tab eventKey="morph" title="Morph">
                      <MorphSelect
                        morphs={morphs}
                        morphTyped={(morph) => setUnlistMorph(morph)}
                        getCurrentText={(text) => setUnlistMorph(text)}
                        morph={unlistMorph}
                        id={'add-morph-select'}
                        showError={false}
                      />
                    </Tab>
                    <Tab eventKey={"gene"} title={"Genes"}>
                      <MorphGeneInputs
                        genes={genes}
                        setMorphName={(name) => { }}
                        morphs={morphs}
                        presetGenes={unlistGenes}
                        setCurrentGenes={(genes) => setUnlistGenes(genes)}
                      />
                    </Tab>
                  </Tabs>
                </div>
              </div>
            </Tab>
            <Tab eventKey='photos' title="Photos">
              <div className='mt-3'>
                <SortAndUploadPhotos
                  rearrangeActive={rearrangeActive}
                  rearrangeOrder={rearrangeOrder}
                  setRearrangeOrder={setRearrangeOrder}
                  pictures={editingFamilyIndividual.pictures}
                  saveOrder={saveOrder}
                  createFunction={uploadPhoto}
                  deleteFunction={removePicture}
                  uploadingPicture={uploadingPicture}
                  maxPhotos={editingFamilyIndividual.max_photos}
                />
              </div>
            </Tab>
          </Tabs>

        </div>
      }
      closeModal={closeModal}
    />
  );
};